import { createEvent, getAllEvents } from '../supabase/events';
import { generateTimeOptions } from '../utils/dateTime';
import { getGoogleMapsUrl } from '../utils/geo';
import { getModernPlaceIcon } from '../utils/places';
import { showError } from '../utils/helpers';

export const handleLinkUp = async (placeId, placeName, placePhotoUrl, placeUrl, placeAddress, placeLat, placeLng, placeIcon, placeType) => {
    // Calculate dates BEFORE creating HTML
const today = new Date();
const todayStr = today.toISOString().split('T')[0];
// Start date: maximum 30 days from today
const maxStartDate = new Date(today);
maxStartDate.setDate(maxStartDate.getDate() + 30);
const maxStartDateStr = maxStartDate.toISOString().split('T')[0];

    // Generate the proper SVG icon based on place type
const modernIconSVG = getModernPlaceIcon(placeType || 'place');
const iconDataURL = `data:image/svg+xml,${encodeURIComponent(modernIconSVG)}`;

// Store place coordinates and icon globally for the form submission
window.currentPlaceLocation = { lat: placeLat, lng: placeLng };
window.currentPlaceIcon = iconDataURL; // Store the full SVG data URL
window.currentPlaceType = placeType || 'place'; // Store detected category for event creation

  // Get current user
  const userId = window.currentAppUser?.uid || window.currentUser?.uid;
  
  if (!userId) {
    window.showToast('Please log in to create events', 'error');
    return;
  }
  
  // Check daily limit before showing the form
  const limitCheck = await window.checkDailyEventLimit(userId);
  
// Close the place info window
if (window.currentInfoWindow) {
  window.currentInfoWindow.close();
}

// Close search pin popup if it exists
if (window.searchPinRef.current) {
  try {
    window.searchPinRef.current.closePopup();
  } catch (e) {
    console.error('No search pin popup to close');
  }
}
  
  // Create modal overlay
  const modalOverlay = document.createElement('div');
  modalOverlay.id = 'linkUpModal';
  modalOverlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 20px;
  `;
  
  modalOverlay.innerHTML = `
<div style="
background: white;
border-radius: 16px;
max-width: 500px;
width: 100%;
max-height: 90vh;
overflow-y: auto;
box-shadow: 0 10px 40px rgba(0,0,0,0.3);
border: 2px solid #000000;
position: relative;
${!limitCheck.allowed ? 'filter: blur(8px); pointer-events: none;' : ''}
">
      <div style="padding: 24px;">
        <!-- Header -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
          <h2 style="margin: 0; font-size: 24px; font-weight: 600; color: #202124;">Linking Event</h2>
          <button 
            onclick="document.getElementById('linkUpModal').remove()"
            style="
              background: none;
              border: none;
              font-size: 28px;
              color: #5f6368;
              cursor: pointer;
              padding: 0;
              width: 32px;
              height: 32px;
              display: flex;
              align-items: center;
              justify-content: center;
            "
          >×</button>
        </div>
        
<!-- Place Info -->
<div style="display: flex; align-items: start; gap: 12px; margin-bottom: 24px; padding: 12px; background: #f8f9fa; border-radius: 12px; border: 2px solid #000000; box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15);">
<img 
  src="${placePhotoUrl}" 
  alt="${placeName}" 
  style="width: 48px; height: 48px; border-radius: 8px; object-fit: cover; flex-shrink: 0;"
/>
<div style="flex: 1; min-width: 0; padding-right: 8px;">
  <h3 style="margin: 0 0 6px 0; font-size: 16px; font-weight: 600; color: #202124; line-height: 1.3; word-wrap: break-word;">
    ${placeName}
  </h3>
  <div style="display: flex; gap: 4px; align-items: start;">
    <span style="font-size: 12px; color: #5f6368; flex-shrink: 0;">📍</span>
    <p style="margin: 0; font-size: 12px; color: #5f6368; word-wrap: break-word; overflow-wrap: break-word; line-height: 1.4; flex: 1;">
      ${placeAddress}
    </p>
  </div>
</div>
<button
onclick="(window.openExternal||window.open)('${getGoogleMapsUrl(placeId, placeLat, placeLng)}', '_blank')"
  style="
    width: 36px;
    height: 36px;
    background: #1967D2;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.15);
    transition: all 0.2s;
    padding: 4px;
    flex-shrink: 0;
  "
  onmouseover="this.style.background='#1557B0'; this.style.boxShadow='0 3px 8px rgba(0,0,0,0.25)'; this.style.transform='scale(1.05)'"
  onmouseout="this.style.background='#1967D2'; this.style.boxShadow='0 2px 4px rgba(0,0,0,0.15)'; this.style.transform='scale(1)'"
  title="View in Google Maps"
>
<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
  <polygon points="3 11 22 2 13 21 11 13 3 11"></polygon>
</svg>
</button>
</div>
        
        <!-- Form -->
        <form id="linkUpForm"> 
${placeId.startsWith('custom_') ? `
<!-- Event Image Upload (custom pins only) -->
<div style="margin-bottom: 20px;">
  <label style="display: block; font-size: 14px; font-weight: 500; color: #202124; margin-bottom: 8px;">
    Event Image <span style="color: #5f6368; font-weight: 400;">(optional)</span>
  </label>
  <input 
    type="file"
    id="customEventImage_${placeId}"
    name="customEventImage"
    accept="image/*"
    style="display: none;"
    onchange="handleCustomEventImageUpload(event, '${placeId}')"
  />
  <!-- Circle preview (hidden until image selected) -->
  <div id="imagePreview_${placeId}" style="display: none; flex-direction: column; align-items: center; gap: 6px; margin-bottom: 10px;">
    <div style="position: relative; width: 100px; height: 100px;">
      <div style="width: 100px; height: 100px; border-radius: 50%; overflow: hidden; border: 3px solid #4285f4; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
        <img id="previewImg_${placeId}" src="" style="width: 100%; height: 100%; object-fit: cover; display: block;" />
      </div>
      <button
        type="button"
        onclick="clearCustomEventImage('${placeId}')"
        style="position: absolute; top: -2px; right: -2px; background: #ea4335; border: 2px solid white; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: white; font-size: 12px; line-height: 1;"
        title="Remove image"
      >✕</button>
    </div>
    <p style="margin: 0; font-size: 11px; color: #9e9e9e;">Preview on marker</p>
  </div>
  <!-- Upload button -->
  <button
    type="button"
    ${!limitCheck.allowed ? 'disabled' : ''}
    onclick="window._triggerImagePicker('customEventImage_${placeId}', (e) => window.handleCustomEventImageUpload(e, '${placeId}'), { maxSize: 600, quality: 90 })"
    style="
      width: 100%;
      padding: 12px;
      border: 2px dashed #dadce0;
      border-radius: 8px;
      background: #f8f9fa;
      cursor: pointer;
      transition: all 0.2s;
      ${!limitCheck.allowed ? 'background: #f5f5f5; color: #9e9e9e; cursor: not-allowed;' : ''}
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    "
    onmouseover="if (!this.disabled) { this.style.borderColor='#4285f4'; this.style.background='#f1f3f4'; }"
    onmouseout="if (!this.disabled) { this.style.borderColor='#dadce0'; this.style.background='#f8f9fa'; }"
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <circle cx="8.5" cy="8.5" r="1.5"></circle>
      <polyline points="21 15 16 10 5 21"></polyline>
    </svg>
    <span id="uploadText_${placeId}" style="font-size: 14px; color: #5f6368; font-weight: 500;">
      Choose Image
    </span>
  </button>
</div>
` : ''}

<!-- Label Input (only for custom locations) -->
${placeId.startsWith('custom_') ? `
<div style="margin-bottom: 20px;">
  <label style="display: block; font-size: 14px; font-weight: 500; color: #202124; margin-bottom: 8px;">
    Label<span style="color: #000; margin-left: 2px;">*</span> <span style="color: #5f6368; font-weight: 400;">(visible on event marker)</span>
  </label>
  <input 
    type="text"
    name="customLabel"
    maxlength="37"
    placeholder="e.g., Beach Volleyball, Park Meetup"
    ${!limitCheck.allowed ? 'disabled' : ''}
    style="
      width: 100%;
      padding: 12px;
      border: 2px solid #dadce0;
      border-radius: 8px;
      font-size: 14px;
      font-family: 'Roboto', Arial, sans-serif;
      box-sizing: border-box;
      ${!limitCheck.allowed ? 'background: #f5f5f5; color: #9e9e9e; cursor: not-allowed;' : ''}
    "
    onfocus="this.style.borderColor='#4285f4'; this.parentElement.querySelector('.error-message')?.remove();"
    onblur="this.style.borderColor='#dadce0'"
  />
</div>
` : ''}

          <!-- Title Input -->
          <div style="margin-bottom: 20px;">
            <label style="display: block; font-size: 14px; font-weight: 500; color: #202124; margin-bottom: 8px;">
              Title<span style="color: #000; margin-left: 2px;">*</span>
            </label>
            <input 
              type="text"
              name="title"
              maxlength="60"
              placeholder="e.g., Coffee & Chat Meetup"
              ${!limitCheck.allowed ? 'disabled' : ''}
              style="
                width: 100%;
                padding: 12px;
                border: 2px solid #dadce0;
                border-radius: 8px;
                font-size: 14px;
                font-family: 'Roboto', Arial, sans-serif;
                box-sizing: border-box;
                ${!limitCheck.allowed ? 'background: #f5f5f5; color: #9e9e9e; cursor: not-allowed;' : ''}
              "
onfocus="this.style.borderColor='#4285f4'; this.parentElement.querySelector('.error-message')?.remove();"
onblur="this.style.borderColor='#dadce0'"
            />
          </div>
          
          <!-- Description Input -->
          <div style="margin-bottom: 20px;">
            <label style="display: block; font-size: 14px; font-weight: 500; color: #202124; margin-bottom: 8px;">
              Description<span style="color: #000; margin-left: 2px;">*</span>
            </label>
            <textarea 
              name="description"
              rows="8"
              maxlength="1500"
              placeholder="Describe your event..."
              ${!limitCheck.allowed ? 'disabled' : ''}
              style="
                width: 100%;
                padding: 12px;
                border: 2px solid #dadce0;
                border-radius: 8px;
                font-size: 14px;
                font-family: 'Roboto', Arial, sans-serif;
                resize: vertical;
                box-sizing: border-box;
                ${!limitCheck.allowed ? 'background: #f5f5f5; color: #9e9e9e; cursor: not-allowed;' : ''}
              "
onfocus="this.style.borderColor='#4285f4'; this.parentElement.querySelector('.error-message')?.remove();"
onblur="this.style.borderColor='#dadce0'"
            ></textarea>
          </div>
          
          <!-- Start Date and Time -->
          <div style="margin-bottom: 20px;">
            <label style="display: block; font-size: 14px; font-weight: 500; color: #202124; margin-bottom: 8px;">
              Start<span style="color: #000; margin-left: 2px;">*</span>
            </label>
            <div style="display: flex; gap: 12px;">
              <div style="flex: 1;">
                <label style="display: block; font-size: 12px; color: #5f6368; margin-bottom: 6px;">
                  Date<span style="color: #000; margin-left: 2px;">*</span>
                </label>
<input 
type="date"
${!limitCheck.allowed ? 'disabled' : ''}
name="startDate"
id="startDate"
min="${todayStr}"
max="${maxStartDateStr}"
                  style="
                    width: 100%;
                    padding: 12px;
                    border: 2px solid #dadce0;
                    border-radius: 8px;
                    font-size: 14px;
                    font-family: 'Roboto', Arial, sans-serif;
                    box-sizing: border-box;
                    ${!limitCheck.allowed ? 'background: #f5f5f5; color: #9e9e9e; cursor: not-allowed;' : ''}
                    cursor: pointer;
                  "
onfocus="
  this.style.borderColor='#4285f4'; 
  this.parentElement.parentElement.parentElement.querySelector('.error-message')?.remove();
  const today = new Date().toISOString().split('T')[0];
  const startDate = document.getElementById('startDate')?.value;
  this.min = startDate && startDate >= today ? startDate : today;
"
onblur="this.style.borderColor='#dadce0'"
onchange="updateStartDateMax(); updateEndTimeMin(); validateEventDuration()"
                />
              </div>
              
              <div style="flex: 1;">
                <label style="display: block; font-size: 12px; color: #5f6368; margin-bottom: 6px;">
                  Time<span style="color: #000; margin-left: 2px;">*</span>
                </label>
                <select
                  name="startTime"
                  id="startTime"
                  ${!limitCheck.allowed ? 'disabled' : ''}
                  style="
                    width: 100%;
                    padding: 12px;
                    border: 2px solid #dadce0;
                    border-radius: 8px;
                    font-size: 14px;
                    font-family: 'Roboto', Arial, sans-serif;
                    box-sizing: border-box;
                    ${!limitCheck.allowed ? 'background: #f5f5f5; color: #9e9e9e; cursor: not-allowed;' : ''}
                    cursor: pointer;
                    background: white;
                  "
onfocus="this.style.borderColor='#4285f4'; focusCurrentTime('startTime'); this.parentElement.parentElement.parentElement.querySelector('.error-message')?.remove();"
onblur="this.style.borderColor='#dadce0'"
onchange="updateEndTimeMin()"
>
${/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ? '<option value=""></option>' : '<option value="">Select time</option>'}
${generateTimeOptions()}
</select>
              </div>
            </div>
          </div>
          
<!-- End Date and Time -->
<div style="margin-bottom: 20px;">
<label style="display: block; font-size: 14px; font-weight: 500; color: #202124; margin-bottom: 8px;">
  End<span style="color: #000; margin-left: 2px;">*</span>
</label>
<div style="display: flex; gap: 12px;">
  <div style="flex: 1;">
<label style="display: block; font-size: 12px; color: #5f6368; margin-bottom: 6px;">
Date<span style="color: #000; margin-left: 2px;">*</span>
</label>
<input 
type="date"
name="endDate"
${!limitCheck.allowed ? 'disabled' : ''}
id="endDate"
min="${todayStr}"
max=""
      style="
        width: 100%;
        padding: 12px;
        border: 2px solid #dadce0;
        border-radius: 8px;
        font-size: 14px;
        font-family: 'Roboto', Arial, sans-serif;
        box-sizing: border-box;
        ${!limitCheck.allowed ? 'background: #f5f5f5; color: #9e9e9e; cursor: not-allowed;' : ''}
        cursor: pointer;
      " 
onfocus="
  this.style.borderColor='#4285f4';
  const today = new Date().toISOString().split('T')[0];
  const startDate = document.getElementById('startDate')?.value;
  
  // Min is start date or today
  this.min = startDate && startDate >= today ? startDate : today;
  
  // Max is 7 days after start date
  if (startDate) {
    const start = new Date(startDate);
    const maxEnd = new Date(start);
    maxEnd.setDate(maxEnd.getDate() + 7);
    this.max = maxEnd.toISOString().split('T')[0];
  }
"
onblur="this.style.borderColor='#dadce0'"
onchange="updateStartDateMax(); updateEndTimeMin(); validateEventDuration()"
    />
  </div>
  
  <div style="flex: 1;">
<label style="display: block; font-size: 12px; color: #5f6368; margin-bottom: 6px;">
Time<span style="color: #000; margin-left: 2px;">*</span>
</label>
    <select
      name="endTime"
      id="endTime"
      ${!limitCheck.allowed ? 'disabled' : ''}
      style="
        width: 100%;
        padding: 12px;
        border: 2px solid #dadce0;
        border-radius: 8px;
        font-size: 14px;
        font-family: 'Roboto', Arial, sans-serif;
        box-sizing: border-box;
        ${!limitCheck.allowed ? 'background: #f5f5f5; color: #9e9e9e; cursor: not-allowed;' : ''}
        cursor: pointer;
        background: white;
      "
onfocus="this.style.borderColor='#4285f4'; focusCurrentTime('endTime'); this.parentElement.parentElement.parentElement.querySelector('.error-message')?.remove();"
onblur="this.style.borderColor='#dadce0'"
onchange="updateEndTimeMin(); validateEventDuration()"
>
${/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ? '<option value=""></option>' : '<option value="">Select time</option>'}
${generateTimeOptions()}
</select>
  </div>
</div>
</div>
          
<!-- People Limit -->
<div style="margin-bottom: 24px;">
<label style="display: block; font-size: 14px; font-weight: 500; color: #202124; margin-bottom: 8px;">
  People limit<span style="color: #000; margin-left: 2px;">*</span> <span style="color: #5f6368; font-weight: 400;">(2 - 200)</span>
</label>
<input 
  type="number"
  name="peopleLimit"
  id="peopleLimit"
  min="2"
  max="200"
  placeholder="e.g., 10"
  ${!limitCheck.allowed ? 'disabled' : ''}
  style="
    width: 100%;
    padding: 12px;
    border: 2px solid #dadce0;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Roboto', Arial, sans-serif;
    box-sizing: border-box;
    ${!limitCheck.allowed ? 'background: #f5f5f5; color: #9e9e9e; cursor: not-allowed;' : ''}
  "
  onfocus="this.style.borderColor='#4285f4'; window.clearPeopleLimitError(this);"
  onblur="this.style.borderColor='#dadce0';"
  oninput="this.setCustomValidity(''); window.clearPeopleLimitError(this);"
  oninvalid="event.preventDefault(); this.setCustomValidity(''); window.validatePeopleLimit(this);"
/>

<!-- No Limit Checkbox -->
<div style="margin-top: 12px;">
<div style="
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #5f6368;
">
  <input 
    type="checkbox"
    id="noLimitCheckbox"
    name="noLimit"
    onchange="window.toggleNoLimit(this);"
    style="
      width: 18px;
      height: 18px;
      cursor: pointer;
      margin: 0;
      accent-color: #008fa1;
    "
  />
  <span style="user-select: none; pointer-events: none; cursor: default;">No Limit</span>
</div>
</div>
</div>

<!-- Gender Preference -->
<div style="margin-bottom: 16px;">
<label style="display: block; font-size: 14px; font-weight: 500; color: #202124; margin-bottom: 8px;">
  Open for <span style="color: #000;">*</span>
</label>
<div id="genderPreferenceButtons" style="display: flex; gap: 8px; flex-wrap: wrap;">
  <button
    type="button"
    data-gender="man"
    data-selected="false"
    onclick="
      const btn = this;
      const currentlySelected = btn.dataset.selected === 'true';
      btn.dataset.selected = (!currentlySelected).toString();
      btn.style.background = !currentlySelected ? '#dbeafe' : '#f3f4f6';
      btn.style.borderColor = !currentlySelected ? '#3b82f6' : '#d1d5db';
      btn.style.color = !currentlySelected ? '#1d4ed8' : '#6b7280';
      window.updateEventGenderPreference();
      const container = document.getElementById('genderPreferenceButtons');
      if (container) {
        const error = container.parentElement.querySelector('.error-message');
        if (error) error.remove();
        container.querySelectorAll('button').forEach(b => {
          if (b.dataset.selected !== 'true') b.style.borderColor = '#d1d5db';
        });
      }
    "
    style="
      flex: 1;
      min-width: 80px;
      padding: 10px 12px;
      border: 2px solid #d1d5db;
      border-radius: 10px;
      background: #f3f4f6;
      color: #6b7280;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
    "
  >
    <span>👨</span> Man
  </button>
  <button
    type="button"
    data-gender="woman"
    data-selected="false"
    onclick="
      const btn = this;
      const currentlySelected = btn.dataset.selected === 'true';
      btn.dataset.selected = (!currentlySelected).toString();
      btn.style.background = !currentlySelected ? '#fce7f3' : '#f3f4f6';
      btn.style.borderColor = !currentlySelected ? '#ec4899' : '#d1d5db';
      btn.style.color = !currentlySelected ? '#be185d' : '#6b7280';
      window.updateEventGenderPreference();
      const container = document.getElementById('genderPreferenceButtons');
      if (container) {
        const error = container.parentElement.querySelector('.error-message');
        if (error) error.remove();
        container.querySelectorAll('button').forEach(b => {
          if (b.dataset.selected !== 'true') b.style.borderColor = '#d1d5db';
        });
      }
    "
    style="
      flex: 1;
      min-width: 80px;
      padding: 10px 12px;
      border: 2px solid #d1d5db;
      border-radius: 10px;
      background: #f3f4f6;
      color: #6b7280;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
    "
  >
    <span>👩</span> Woman
  </button>
  <button
    type="button"
    data-gender="different"
    data-selected="false"
    onclick="
      const btn = this;
      const currentlySelected = btn.dataset.selected === 'true';
      btn.dataset.selected = (!currentlySelected).toString();
      btn.style.background = !currentlySelected ? '#f3e8ff' : '#f3f4f6';
      btn.style.borderColor = !currentlySelected ? '#a855f7' : '#d1d5db';
      btn.style.color = !currentlySelected ? '#7c3aed' : '#6b7280';
      window.updateEventGenderPreference();
      const container = document.getElementById('genderPreferenceButtons');
      if (container) {
        const error = container.parentElement.querySelector('.error-message');
        if (error) error.remove();
        container.querySelectorAll('button').forEach(b => {
          if (b.dataset.selected !== 'true') b.style.borderColor = '#d1d5db';
        });
      }
    "
    style="
      flex: 1;
      min-width: 80px;
      padding: 10px 12px;
      border: 2px solid #d1d5db;
      border-radius: 10px;
      background: #f3f4f6;
      color: #6b7280;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
    "
  >
    <span>🌈</span> Different
  </button>
</div>
<p style="font-size: 11px; color: #9ca3af; margin-top: 6px;">Select who can see and join this event</p>
</div>

<!-- Category Picker (always shown, multi-select max 3) -->
<div style="margin-bottom: 20px;">
<label style="
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #202124;
  margin-bottom: 4px;
">
  Event Category <span style="color: #000;">*</span>
</label>
<p id="categoryPickerCount" style="font-size: 11px; color: #5f6368; margin: 0 0 8px 0;">
  Select up to 3 categories
</p>
<div id="categoryPickerButtons" style="display: flex; gap: 6px; flex-wrap: wrap;">
</div>
</div>

<!-- Age Limit Range Slider -->
<div style="margin-bottom: 20px;">
<label style="
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #202124;
  margin-bottom: 4px;
">
  Age Limit
</label>
<p id="ageLimitLabel" style="font-size: 11px; color: #5f6368; margin: 0 0 12px 0;">
  18 - 99 (all ages)
</p>
<div style="position: relative; height: 40px; padding: 0 8px;">
  <div style="position: absolute; top: 50%; left: 8px; right: 8px; height: 4px; background: #e0e0e0; border-radius: 2px; transform: translateY(-50%);">
    <div id="ageLimitTrack" style="position: absolute; height: 100%; background: #2c2c2c; border-radius: 2px; left: 0%; right: 0%;"></div>
  </div>
  <input type="range" id="ageLimitMin" min="18" max="99" value="18"
    style="position: absolute; top: 50%; left: 8px; right: 8px; width: calc(100% - 16px); transform: translateY(-50%); -webkit-appearance: none; appearance: none; background: transparent; pointer-events: none; z-index: 3; margin: 0; height: 20px;"
    oninput="window.updateAgeLimitSlider()"
  />
  <input type="range" id="ageLimitMax" min="18" max="99" value="99"
    style="position: absolute; top: 50%; left: 8px; right: 8px; width: calc(100% - 16px); transform: translateY(-50%); -webkit-appearance: none; appearance: none; background: transparent; pointer-events: none; z-index: 4; margin: 0; height: 20px;"
    oninput="window.updateAgeLimitSlider()"
  />
</div>
</div>

<!-- Require Approve Checkbox -->
<div style="margin-bottom: 24px;">
<div style="
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #5f6368;
">
  <input 
    type="checkbox"
    id="requireApproveCheckbox"
    name="requireApprove"
    style="
      width: 18px;
      height: 18px;
      cursor: pointer;
      margin: 0;
      accent-color: #008fa1;
    "
  />
  <span style="user-select: none; cursor: default;">Require Approve</span>
  <button
    type="button"
    onclick="window.showRequireApproveInfo(event)"
    style="
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      margin-left: 4px;
      display: flex;
      align-items: center;
    "
    title="Learn more about Require Approve"
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2">
      <circle cx="12" cy="12" r="10"></circle>
      <path d="M12 16v-4"></path>
      <circle cx="12" cy="8" r="0.5" fill="#5f6368"></circle>
    </svg>
  </button>
</div>
</div>
          
<!-- Submit Button -->
<button
id="linkUpSubmitBtn"
type="button"
${!limitCheck.allowed ? 'disabled' : ''}
onclick="
  const form = document.getElementById('linkUpForm');
  if (!form || this.disabled) return;
  form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
"
style="
  width: 100%;
  background: ${!limitCheck.allowed ? '#e0e0e0' : '#2c2c2c'};
  color: ${!limitCheck.allowed ? '#9e9e9e' : 'white'};
  border: none;
  padding: 14px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${!limitCheck.allowed ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1);
"
${limitCheck.allowed ? `
onmouseover="this.style.background='#1a1a1a'; this.style.boxShadow='0 6px 16px rgba(0, 0, 0, 0.2), 0 3px 8px rgba(0, 0, 0, 0.15)'; this.style.transform='translateY(-1px)'"
onmouseout="this.style.background='#2c2c2c'; this.style.boxShadow='0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1)'; this.style.transform='translateY(0)'"
` : ''}
>
${!limitCheck.allowed ? '🔒 Limit Reached' : 'Create Event'}
</button>
        </form>
      </div>
    </div>
          ${!limitCheck.allowed ? `
    <!-- Limit Reached Overlay -->
    <div style="
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      border-radius: 16px;
      padding: 32px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.3);
      border: 2px solid #ea4335;
      text-align: center;
      max-width: 400px;
      width: calc(100% - 40px);
      z-index: 10001;
    ">
      <div style="font-size: 64px;">🚫</div>
      <h3 style="
        margin: 0 0 12px 0;
        font-size: 20px;
        font-weight: 600;
        color: #202124;
      ">Daily Limit Reached</h3>
      <p style="
        margin: 0 0 24px 0;
        font-size: 14px;
        color: #5f6368;
        line-height: 1.6;
      ">
        You've created <strong>${limitCheck.count} events</strong> today.<br>
        Please wait until tomorrow to create more events!
      </p>
      <button 
        onclick="document.getElementById('linkUpModal').remove()"
        style="
          background: #00a3b7;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 12px 32px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
          width: 100%;
        "
        onmouseover="this.style.background='#008a9cff'"
        onmouseout="this.style.background='#00a3b7'"
      >
        Close
      </button>
    </div>
    ` : ''}
  `;
  
  document.body.appendChild(modalOverlay);

// Pre-select categories based on detected place type
  const normalizedType = window.normalizeCategory ? window.normalizeCategory(placeType || 'place') : 'place';
  if (placeType && placeType !== 'place' && !placeId.startsWith('custom_')) {
    window.currentEventCategories = [normalizedType];
  } else {
window.currentEventCategories = [];
  }
  // Reset age limit
  window.currentAgeMin = 18;
  window.currentAgeMax = 99;
  // Populate category picker buttons
  const catContainer = document.getElementById('categoryPickerButtons');
  if (catContainer) {
const categories = window.EVENT_CATEGORIES || [
      { key: 'food_drink', icon: '🍽️', label: 'Food & Drink' },
      { key: 'nightlife', icon: '🌙', label: 'Nightlife' },
      { key: 'outdoors', icon: '🌿', label: 'Outdoors' },
      { key: 'active', icon: '⚡', label: 'Active' },
      { key: 'shopping', icon: '🛍️', label: 'Shopping' },
      { key: 'entertainment', icon: '🎭', label: 'Entertainment' },
    ];
catContainer.innerHTML = categories.map(function(cat) {
      var isSelected = window.currentEventCategories.includes(cat.key);
      return '<button type="button" data-category="' + cat.key + '" style="padding: 6px 12px; border: 2px solid ' + (isSelected ? '#2c2c2c' : '#d1d5db') + '; border-radius: 20px; background: ' + (isSelected ? '#f0f0f0' : 'white') + '; font-size: 12px; font-weight: ' + (isSelected ? '600' : '400') + '; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 4px; white-space: nowrap;"><span>' + cat.icon + '</span><span>' + cat.label + '</span></button>';
    }).join('');
    // Attach click handlers via addEventListener (avoids emoji/quote issues in onclick attributes)
catContainer.querySelectorAll('button[data-category]').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.stopPropagation();
        window.selectEventCategory(this.dataset.category);
      });
    });
    // Update counter if preselected
    var counter = document.getElementById('categoryPickerCount');
    if (counter && window.currentEventCategories.length > 0) {
      counter.textContent = window.currentEventCategories.length + '/3 selected';
    }
  }

// Attach form submit handler with proper closure
setTimeout(() => {
  const form = document.getElementById('linkUpForm');
  if (form) {
    const submitHandler = (event) => {
      console.log('🎯 SUBMIT HANDLER TRIGGERED');
      event.preventDefault();
      event.stopPropagation();
      
if (limitCheck.allowed) {
  // 🆕 Get current user from window or React context
  const currentUserData = window.currentAppUser || window.currentUser;
  window.handleLinkUpSubmit(event, placeId, placeName, placeAddress, placePhotoUrl, currentUserData);
}
    };
    
    form.addEventListener('submit', submitHandler);
  }
}, 100);
};

export const handleLinkUpSubmit = async (event, placeId, placeName, placeAddress, placePhotoUrl, currentUserData) => {
  event.preventDefault();
  
  if (!currentUserData) {
    window.showToast('User data not found. Please refresh and try again.', 'error');
    return;
  }

  // Get form elements
  const titleInput = event.target.querySelector('[name="title"]');
  const descInput = event.target.querySelector('[name="description"]');
  const customLabelInput = event.target.querySelector('[name="customLabel"]');
  const startDateInput = document.getElementById('startDate');
  const startTimeInput = document.getElementById('startTime');
  const endDateInput = document.getElementById('endDate');
  const endTimeInput = document.getElementById('endTime');
  const peopleLimitInput = event.target.querySelector('[name="peopleLimit"]');
  const noLimitCheckbox = event.target.querySelector('[name="noLimit"]');

  // CRITICAL: Clear ALL previous error messages EVERY TIME
  document.querySelectorAll('.error-message').forEach(el => el.remove());
  
// CRITICAL: Reset ALL border colors EVERY TIME
  [titleInput, descInput, customLabelInput, startDateInput, startTimeInput, endDateInput, endTimeInput, peopleLimitInput]
    .filter(input => input)
    .forEach(input => {
      if (input) input.style.borderColor = '#dadce0';
    });
  
  // Reset gender buttons border colors
  const genderButtonsContainer = document.getElementById('genderPreferenceButtons');
  if (genderButtonsContainer) {
    genderButtonsContainer.querySelectorAll('button').forEach(btn => {
      if (btn.dataset.selected !== 'true') {
        btn.style.borderColor = '#d1d5db';
      }
    });
  }

  let hasError = false;

  // Validate title (trim whitespace!)
  if (!titleInput.value || titleInput.value.trim() === '') {
    showError(titleInput, 'This field is required');
    hasError = true;
  }

  // Validate custom label (only for custom locations)
  if (customLabelInput && (!customLabelInput.value || customLabelInput.value.trim() === '')) {
    showError(customLabelInput, 'This field is required');
    hasError = true;
  }

  // Validate description (trim whitespace!)
  if (!descInput.value || descInput.value.trim() === '') {
    showError(descInput, 'This field is required');
    hasError = true;
  }

  // Validate start date and time
  if (!startDateInput.value || !startTimeInput.value) {
    const startContainer = startDateInput.closest('div[style*="margin-bottom: 20px"]');
    
    const errorDiv = document.createElement('p');
    errorDiv.className = 'error-message';
    errorDiv.style.cssText = `
      font-size: 11px;
      color: #ea4335;
      margin-top: 4px;
      margin-bottom: 0;
    `;
    errorDiv.textContent = 'Both date and time are required';
    
    if (startContainer) {
      startContainer.appendChild(errorDiv);
    }
    
    if (!startDateInput.value) startDateInput.style.borderColor = '#ea4335';
    if (!startTimeInput.value) startTimeInput.style.borderColor = '#ea4335';
    
    hasError = true;
  }

  // Validate end date and time
  if (!endDateInput.value || !endTimeInput.value) {
    const endContainer = endDateInput.closest('div[style*="margin-bottom: 20px"]');
    
    const errorDiv = document.createElement('p');
    errorDiv.className = 'error-message';
    errorDiv.style.cssText = `
      font-size: 11px;
      color: #ea4335;
      margin-top: 4px;
      margin-bottom: 0;
    `;
    errorDiv.textContent = 'Both date and time are required';
    
    if (endContainer) {
      endContainer.appendChild(errorDiv);
    }
    
    if (!endDateInput.value) endDateInput.style.borderColor = '#ea4335';
    if (!endTimeInput.value) endTimeInput.style.borderColor = '#ea4335';
    
    hasError = true;
  }

// Validate people limit
  const peopleLimitValue = peopleLimitInput?.value;
  const isNoLimitChecked = noLimitCheckbox?.checked;

  if (!isNoLimitChecked) {
    if (!peopleLimitValue || peopleLimitValue.trim() === '') {
      showError(peopleLimitInput, 'Please enter a limit or check "No Limit"');
      hasError = true;
    } else {
      const numValue = parseInt(peopleLimitValue);
      
      if (isNaN(numValue) || numValue < 2 || numValue > 200) {
        showError(peopleLimitInput, 'Limit must be between 2 and 200');
        hasError = true;
      }
    }
}
  
// Validate gender preference - at least one must be selected
// Read directly from DOM to ensure we get the current state
const genderContainer = document.getElementById('genderPreferenceButtons');
const selectedGenders = [];
if (genderContainer) {
  genderContainer.querySelectorAll('button').forEach(btn => {
    const gender = btn.getAttribute('data-gender');
    const isSelected = btn.getAttribute('data-selected');
    if (isSelected === 'true' && gender) {
      selectedGenders.push(gender);
    }
  });
}
// Update the global variable with the correct value
window.currentEventGenderPreference = selectedGenders;

if (selectedGenders.length === 0) {
  if (genderContainer) {
    // Remove any existing error
    const existingError = genderContainer.parentElement.querySelector('.error-message');
    if (existingError) existingError.remove();
    
    // Create error message
    const errorDiv = document.createElement('p');
    errorDiv.className = 'error-message';
    errorDiv.style.cssText = `
      font-size: 11px;
      color: #ea4335;
      margin-top: 4px;
      margin-bottom: 0;
      animation: slideDown 0.2s ease-out;
    `;
    errorDiv.textContent = 'Please select at least one option';
    
    // Insert after the buttons container
    genderContainer.insertAdjacentElement('afterend', errorDiv);
    
    // Add red border to all unselected buttons
    genderContainer.querySelectorAll('button').forEach(btn => {
      if (btn.getAttribute('data-selected') !== 'true') {
        btn.style.borderColor = '#ea4335';
      }
    });
  }
hasError = true;
}

// Validate category - at least one must be selected
const selectedCategories = window.currentEventCategories || [];
if (selectedCategories.length === 0) {
  const catContainer = document.getElementById('categoryPickerButtons');
  if (catContainer) {
    const existingError = catContainer.parentElement.querySelector('.error-message');
    if (existingError) existingError.remove();
    
    const errorDiv = document.createElement('p');
    errorDiv.className = 'error-message';
    errorDiv.style.cssText = 'font-size: 11px; color: #ea4335; margin-top: 4px; margin-bottom: 0;';
    errorDiv.textContent = 'Please select at least one category';
    catContainer.insertAdjacentElement('afterend', errorDiv);
  }
  hasError = true;
}

  // If any validation failed, stop here
  if (hasError) {
    return;
  }

  // Validation passed — now disable the button and show spinner
  const submitBtn = document.getElementById('linkUpSubmitBtn');
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.style.cursor = 'not-allowed';
    submitBtn.style.opacity = '0.8';
    submitBtn.innerHTML = '<span style="display:inline-flex;align-items:center;gap:8px;"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="animation:spin 0.8s linear infinite;flex-shrink:0"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>Creating\u2026</span>';
    if (!document.getElementById('linkup-spin-style')) {
      const s = document.createElement('style');
      s.id = 'linkup-spin-style';
      s.textContent = '@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}';
      document.head.appendChild(s);
    }
  }
  
  const formData = new FormData(event.target);
  
  // Validate all required fields
  const startDate = formData.get('startDate');
  const endDate = formData.get('endDate');
  const startTime = formData.get('startTime');
  const endTime = formData.get('endTime');
  
  if (!startDate || !endDate || !startTime || !endTime) {
    return;
  }
  
// Build datetime strings without timezone conversion
const startDateTimeStr = `${startDate}T${startTime}:00`;
const endDateTimeStr = `${endDate}T${endTime}:00`;

// Validate end is after start (compare strings directly)
if (endDateTimeStr <= startDateTimeStr) {
  showError(endDateInput.parentElement.parentElement, 'End date/time must be after start date/time');
  return;
}

// Validate duration is not more than 7 days (use Date only for calculation)
const startForCalc = new Date(startDateTimeStr);
const endForCalc = new Date(endDateTimeStr);
const diffInMs = endForCalc - startForCalc;
const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

if (diffInDays > 7) {
  showError(endDateInput.parentElement.parentElement, 'Event duration cannot exceed 7 days (1 week)');
  return;
}

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const noLimit = formData.get('noLimit') === 'on';
const peopleLimit = noLimit ? null : parseInt(formData.get('peopleLimit'));

try {
  const eventData = {
    placeId: placeId,
    placeName: customLabelInput ? customLabelInput.value.trim() : placeName,
    placeAddress: placeAddress,
    placePhotoUrl: window.customEventImage || placePhotoUrl,
    placeIcon: window.currentPlaceIcon || '',
    title: formData.get('title'),
    description: formData.get('description'),
    startDateTime: startDateTimeStr,  // Send string directly, no .toISOString()
    endDateTime: endDateTimeStr,      // Send string directly, no .toISOString()
    timezone: userTimezone,
    peopleLimit: peopleLimit,
    createdBy: currentUserData.profile?.name || 'User',
    organizerProfilePicture: currentUserData.profile?.profilePicture || null,
    creatorId: currentUserData.uid, // Changed from email to uid
    participants: [currentUserData.uid], // Changed from email to uid
    latitude: window.currentPlaceLocation?.lat || window.currentUserLocation?.lat,
    longitude: window.currentPlaceLocation?.lng || window.currentUserLocation?.lng,
    require_approve: document.getElementById('requireApproveCheckbox')?.checked || false,
    pending_requests: [],
gender_preference: window.currentEventGenderPreference,
category: window.currentEventCategories && window.currentEventCategories.length > 0
      ? window.currentEventCategories
      : ['place'],
age_min: window.currentAgeMin || 18,
age_max: window.currentAgeMax || 99,
  };
  
    // Import createEvent from Supabase
    const result = await createEvent(eventData);
  
if (result.success) {
  // Close modal immediately
  const modal = document.getElementById('linkUpModal');
  if (modal) {
    modal.remove();
  }
  
  // Close search pin popup if exists
  if (window.searchPinRef.current) {
    try {
      window.searchPinRef.current.closePopup();
      window.searchPinRef.current.remove();
      window.searchPinRef.current = null;
    } catch (e) {
      console.error('No search pin to remove');
    }
  }
  
  // Clear custom image
  window.customEventImage = null;
  // Reset category selection
window.currentEventCategories = [];
window.currentAgeMin = 18;
window.currentAgeMax = 99;
  window.currentPlaceType = null;
  
  // Clean up any existing intervals
  if (window.cleanupEventIntervals) {
    window.cleanupEventIntervals();
  }

// Dispatch immediately using data we already have — no extra fetch needed
  const newEvent = {
    ...eventData,
    eventId: result.eventId,
  };
  // Also update allEvents cache right now
  if (window.allEvents) {
    window.allEvents = [...window.allEvents, newEvent];
  }
  // Prevent any immediate realtime UPDATE (e.g. from a DB trigger like updated_at)
  // from calling refreshSingleEventMarker and removing the just-created marker.
  window.skipRealtimeUpdate = result.eventId;
  setTimeout(() => { window.skipRealtimeUpdate = null; }, 5000);
  window.dispatchEvent(new CustomEvent('eventCreated', { detail: newEvent }));
}
  
} catch (error) {
  window.showToast('Failed to create event. Please try again.', 'error');
  // Re-enable button on error
  const btn = document.getElementById('linkUpSubmitBtn');
  if (btn) {
    btn.disabled = false;
    btn.style.cursor = 'pointer';
    btn.style.opacity = '1';
    btn.style.background = '#2c2c2c';
    btn.innerHTML = 'Create Event';
  }
}
};

// Handle custom event image upload
export const handleCustomEventImageUpload = (event, placeId) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onloadend = () => {
    const img = new Image();
    img.onload = () => {
      const MAX_SIZE = 600;
      let { width, height } = img;
      if (width > MAX_SIZE || height > MAX_SIZE) {
        if (width > height) { height = Math.round(height * MAX_SIZE / width); width = MAX_SIZE; }
        else { width = Math.round(width * MAX_SIZE / height); height = MAX_SIZE; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);
      const compressed = canvas.toDataURL('image/jpeg', 0.9);

      window.customEventImage = compressed;

      const previewDiv = document.getElementById(`imagePreview_${placeId}`);
      const previewImg = document.getElementById(`previewImg_${placeId}`);
      const uploadText = document.getElementById(`uploadText_${placeId}`);
      if (previewDiv && previewImg && uploadText) {
        previewImg.src = compressed;
        previewDiv.style.display = 'flex';
        uploadText.textContent = 'Replace Image';
      }
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
};

// Clear custom event image
export const clearCustomEventImage = (placeId) => {
  window.customEventImage = null;
  
  const previewDiv = document.getElementById(`imagePreview_${placeId}`);
  const previewImg = document.getElementById(`previewImg_${placeId}`);
  const uploadText = document.getElementById(`uploadText_${placeId}`);
  const fileInput = document.getElementById(`customEventImage_${placeId}`);
  
if (previewDiv) previewDiv.style.display = 'none';
  if (previewImg) previewImg.src = '';
  if (uploadText) uploadText.textContent = 'Choose Image';
  if (fileInput) fileInput.value = '';
  window.customEventImage = null;
};

export const updateStartDateMax = () => {
  const startDateInput = document.getElementById('startDate');
  const endDateInput = document.getElementById('endDate');
  
  if (startDateInput?.value && endDateInput) {
    const startDate = new Date(startDateInput.value);
    
    // End date min is start date
    endDateInput.min = startDateInput.value;
    
    // End date max is 7 days after start
    const maxEnd = new Date(startDate);
    maxEnd.setDate(maxEnd.getDate() + 7);
    endDateInput.max = maxEnd.toISOString().split('T')[0];
    
    // If current end date is beyond max, reset it
    if (endDateInput.value) {
      const endDate = new Date(endDateInput.value);
      if (endDate > maxEnd) {
        endDateInput.value = '';
      }
    }
  }
};

export const updateEndDateMin = () => {
  const startDate = document.getElementById('startDate');
  const endDate = document.getElementById('endDate');
  if (startDate && endDate && startDate.value) {
    endDate.min = startDate.value;
    // Revalidate end time when start date changes
    window.updateEndTimeMin();
  }
};

export const updateEndDateMax = () => {
const startDate = document.getElementById('startDate');
const endDate = document.getElementById('endDate');

if (startDate && endDate && startDate.value) {
  // Calculate date 7 days from start date
  const start = new Date(startDate.value);
  const maxEnd = new Date(start);
  maxEnd.setDate(maxEnd.getDate() + 7);
  
  // Set max attribute
  endDate.max = maxEnd.toISOString().split('T')[0];
}
};

export const updateEndTimeMin = () => {
  const startDate = document.getElementById('startDate');
  const endDate = document.getElementById('endDate');
  const startTime = document.getElementById('startTime');
  const endTime = document.getElementById('endTime');
  
  if (!startDate || !endDate || !startTime || !endTime) return;
  
  // If dates are the same, filter end time options
  if (startDate.value && endDate.value && startDate.value === endDate.value && startTime.value) {
    // Disable options that are before or equal to start time
    const startTimeValue = startTime.value;
    Array.from(endTime.options).forEach(option => {
      if (option.value && option.value <= startTimeValue) {
        option.disabled = true;
      } else {
        option.disabled = false;
      }
    });
    
    // If current end time is invalid, clear it
    if (endTime.value && endTime.value <= startTimeValue) {
      endTime.value = '';
    }
  } else {
    // Different dates - enable all options
    Array.from(endTime.options).forEach(option => {
      option.disabled = false;
    });
  }
};

export const validateEventDuration = () => {
  const startDate = document.getElementById('startDate');
  const endDate = document.getElementById('endDate');
  const startTime = document.getElementById('startTime');
  const endTime = document.getElementById('endTime');
  
  if (!startDate || !endDate || !startTime || !endTime) return;
  
  // Only validate if all fields have values
  if (startDate.value && endDate.value && startTime.value && endTime.value) {
    const startDateTime = new Date(`${startDate.value}T${startTime.value}`);
    const endDateTime = new Date(`${endDate.value}T${endTime.value}`);
    
    // Calculate difference in days
    const diffInMs = endDateTime - startDateTime;
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
    
    // Check if duration exceeds 7 days - silently reset
    if (diffInDays > 7) {
      endDate.value = '';
      endTime.value = '';
      return false;
    }
    
    return true;
  }
};

// Helper function to validate people limit
export const validatePeopleLimit = (input) => {
  const parent = input.closest('div[style*="margin-bottom: 24px"]') || input.parentElement.parentElement;
  const value = parseInt(input.value);
  
  // Remove existing error
  const existingError = parent.querySelector('.error-message');
  if (existingError) {
    existingError.remove();
  }
  
  if (input.value && (value < 2 || value > 200)) {
    input.style.borderColor = '#ff1700';
    
    // Create error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'error-message';
    errorDiv.style.cssText = 'color: rgba(234, 67, 53, 1); font-size: 11px; margin-top: 4px; animation: slideDown 0.2s ease-out;';
    errorDiv.textContent = 'Limit must be between 2 and 200';
    
    // Insert directly after the input (before the checkbox container)
    input.insertAdjacentElement('afterend', errorDiv);
    
    return false;
  }
  
  input.style.borderColor = '#dadce0';
  return true;
};

export const toggleNoLimit = (checkbox) => {
  const input = document.getElementById('peopleLimit');
  const parent = input.closest('div[style*="margin-bottom: 24px"]') || input.parentElement.parentElement;
  
  if (checkbox.checked) {
    input.disabled = true;
    input.style.background = '#f5f5f5';
    input.style.color = '#9e9e9e';
    input.value = '';
    input.removeAttribute('required');
    input.style.borderColor = '#dadce0';
    
    const errorMsg = parent.querySelector('.error-message');
    if (errorMsg) errorMsg.remove();
  } else {
    input.disabled = false;
    input.style.background = 'white';
    input.style.color = '#202124';
    input.setAttribute('required', 'required');
    input.style.borderColor = '#dadce0';
  }
};

// Helper function to clear people limit error
export const clearPeopleLimitError = (input) => {
  const parent = input.closest('div[style*="margin-bottom: 24px"]') || input.parentElement.parentElement;
  const errorMsg = parent.querySelector('.error-message');
  if (errorMsg) errorMsg.remove();
  
  if (input.style.borderColor === 'rgb(255, 23, 0)') {
    input.style.borderColor = '#4285f4';
  }
};

export const showRequireApproveInfo = (e) => {
  e.preventDefault();
  e.stopPropagation();
  
  const modal = document.createElement('div');
  modal.id = 'requireApproveInfoModal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
  `;
  
  modal.innerHTML = `
    <div style="
      background: white;
      border-radius: 16px;
      padding: 24px;
      width: 90%;
      max-width: 400px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    ">
      <h3 style="
        margin: 0 0 16px 0;
        font-size: 20px;
        font-weight: 600;
        color: #202124;
      ">Require Approve</h3>
      
      <p style="
        margin: 0 0 16px 0;
        font-size: 14px;
        color: #5f6368;
        line-height: 1.6;
      ">
        When enabled, users who want to join your event must be approved by you first. 
        You'll receive a notification request that you can accept or decline.
      </p>
      
      <p style="
        margin: 0 0 20px 0;
        font-size: 14px;
        color: #5f6368;
        line-height: 1.6;
      ">
        All pending requests can be viewed in the "Requests" section in your profile.
      </p>
      
      <button
        onclick="document.getElementById('requireApproveInfoModal').remove()"
        style="
          width: 100%;
          padding: 12px 20px;
          background: white;
          color: black;
          border: 2px solid black;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        "
        onmouseover="this.style.background='rgba(207, 207, 207, 1)'"
        onmouseout="this.style.background='white'"
      >
        Got it
      </button>
    </div>
  `;
  
  document.body.appendChild(modal);
  
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      modal.remove();
    }
  });
};

// Get gender preference icon for event display
export const getGenderPreferenceIcon = (genderPreference) => {
  if (!genderPreference || genderPreference.length === 0) {
    genderPreference = ['man', 'woman', 'different'];
  }
  
  const hasMan = genderPreference.includes('man');
  const hasWoman = genderPreference.includes('woman');
  const hasDifferent = genderPreference.includes('different');
  
  // All genders - show everyone icon
  if (hasMan && hasWoman && hasDifferent) {
    return { icon: '👥', label: 'Everyone', color: '#6b7280' };
  }
  
  // Single gender
  if (genderPreference.length === 1) {
    if (hasMan) return { icon: '👨', label: 'Men only', color: '#3b82f6' };
    if (hasWoman) return { icon: '👩', label: 'Women only', color: '#ec4899' };
    if (hasDifferent) return { icon: '🌈', label: 'Different only', color: '#a855f7' };
  }
  
  // Two genders
  if (hasMan && hasWoman) return { icon: '👫', label: 'Men & Women', color: '#6b7280' };
  if (hasMan && hasDifferent) return { icon: '👨🌈', label: 'Men & Different', color: '#3b82f6' };
  if (hasWoman && hasDifferent) return { icon: '👩🌈', label: 'Women & Different', color: '#ec4899' };
  
  return { icon: '👥', label: 'Everyone', color: '#6b7280' };
};

export const updateEventGenderPreference = () => {
  // Only select buttons within the create event form (not edit mode)
  const container = document.getElementById('genderPreferenceButtons');
  
  if (!container) {
    return;
  }
  
  const buttons = container.querySelectorAll('button');
  const selected = [];
  buttons.forEach((btn, index) => {
    const gender = btn.getAttribute('data-gender');
    const isSelected = btn.getAttribute('data-selected');
    if (isSelected === 'true') {
      selected.push(gender);
    }
  });
  window.currentEventGenderPreference = selected;
};

// Initialize as empty - user must select at least one
window.currentEventGenderPreference = [];


window.handleLinkUp = handleLinkUp;
window.handleLinkUpSubmit = handleLinkUpSubmit;
window.handleCustomEventImageUpload = handleCustomEventImageUpload;
window.clearCustomEventImage = clearCustomEventImage;
window.updateStartDateMax = updateStartDateMax;
window.updateEndDateMin = updateEndDateMin;
window.updateEndDateMax = updateEndDateMax;
window.updateEndTimeMin = updateEndTimeMin;
window.validateEventDuration = validateEventDuration;
window.validatePeopleLimit = validatePeopleLimit;
window.toggleNoLimit = toggleNoLimit;
window.clearPeopleLimitError = clearPeopleLimitError;
window.showRequireApproveInfo = showRequireApproveInfo;
window.getGenderPreferenceIcon = getGenderPreferenceIcon;
window.updateEventGenderPreference = updateEventGenderPreference;